import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NAV_GROUPS } from './nav.config';
import { SidebarComponent } from '../components/sidebar/sidebar.component';
import { SyncStatusComponent } from '../../shared/components/sync-status/sync-status.component';
import { ChatWidgetComponent } from '../../chat/chat-widget/chat-widget.component';
import { ReportIssueWidgetComponent } from '../../support/components/report-issue-widget/report-issue-widget.component';
import { ReferralModalComponent } from '../../referral/referral-modal.component';
import { AuthService } from '../../auth/data-access/auth.service';
import { ChatService } from '../../chat/data-access/chat.service';
import { NotificationsService } from '../../shared/service/notifications.service';
import { SubscriptionStore } from '../../subscription/data-access/subscription.store';
import { SidebarStateService } from './sidebar-state.service';
import { ScrollLockService } from '../../shared/service/scroll-lock.service';

@Component({
  selector: 'app-shell-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    SidebarComponent,
    SyncStatusComponent,
    ChatWidgetComponent,
    ReportIssueWidgetComponent,
    ReferralModalComponent
  ],
  templateUrl: './shell.layout.html',
  styleUrl: './shell.layout.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShellLayout {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastrService);
  // Injected so the bell can read its signals; the service self-starts/stops from the
  // auth state (see NotificationsService), so the shell no longer drives its lifecycle.
  private readonly notificationsSvc = inject(NotificationsService);
  private readonly subscriptionStore = inject(SubscriptionStore);
  private readonly chatService = inject(ChatService);
  private readonly sidebarState = inject(SidebarStateService);
  /** Read by the template: the chat FAB steps aside while a dialog is open. */
  protected readonly scrollLock = inject(ScrollLockService);

  /** Mirrors the sidebar's rail mode so the header and content offsets move with it. */
  readonly sidebarCollapsed = this.sidebarState.collapsed;
  // Speed-dial: at rest only a single arrow FAB shows; toggling it reveals the chat + report
  // widgets stacked above. Keeps the corner uncluttered by default.
  readonly fabOpen = signal(false);

  /** Close the speed-dial when clicking anywhere outside the arrow or its widgets. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.fabOpen()) return;
    const target = event.target as HTMLElement | null;
    // Keep it open while interacting with the arrow itself, the chat, the report widget,
    // or anything that exists to open the chat (e.g. Settings → Help & Support → Chat).
    if (target?.closest('.fab-arrow, app-chat-widget, app-report-issue-widget, [data-chat-trigger]')) return;
    this.closeFab();
  }

  toggleFab(): void {
    if (this.fabOpen()) this.closeFab();
    else this.fabOpen.set(true);
  }

  /**
   * Collapse the speed-dial. The chat panel is rendered inside it, so its open flag
   * has to be cleared too — leaving it set would strand the service at isOpen=true
   * with no widget mounted, and every later chatService.open() would then be a
   * no-op (the signal never changes, so nothing re-mounts the widget).
   */
  closeFab(): void {
    this.fabOpen.set(false);
    this.chatService.close();
  }

  // Subscription/payment mode is off by default — when off, the badge and any
  // billing UI stay hidden and the shell looks exactly as it did before.
  readonly subscriptionModeEnabled = this.subscriptionStore.subscriptionModeEnabled;
  readonly planCode = this.subscriptionStore.planCode;
  readonly planName = this.subscriptionStore.planName;
  readonly isPremium = this.subscriptionStore.isPremium;

  readonly navGroups = NAV_GROUPS;
  readonly showMobileNav = signal(false);
  readonly notificationsOpen = signal(false);
  readonly profileMenuOpen = signal(false);
  readonly user = computed(() => this.authService.currentUser());
  readonly userInitials = computed(() => {
    const name = this.user()?.name ?? '';
    if (!name) return 'JD';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || name.slice(0, 2).toUpperCase();
  });
  readonly userName = computed(() => this.user()?.name ?? 'Guest');
  readonly userEmail = computed(() => this.user()?.email ?? '—');

  constructor() {
    // Load the subscription-mode flag + current plan once for the authenticated
    // area. Safe no-op UI-wise while the flag is off.
    this.subscriptionStore.init();

    // The chat can be opened from outside the speed-dial (Settings → Help & Support
    // → Chat). Its widget only exists in the DOM while the dial is open, so expand
    // the dial whenever something asks for the chat — otherwise the request is a
    // no-op and nothing appears on screen.
    effect(() => {
      if (this.chatService.isOpen()) this.fabOpen.set(true);
    });
  }

  goToPricing(): void {
    this.router.navigate(['/app/pricing']);
  }

  // Live (socket) + persisted (REST) notifications from the backend.
  readonly notifications = this.notificationsSvc.notifications;
  readonly unreadCount = this.notificationsSvc.unreadCount;

  toggleMobileNav(): void {
    this.showMobileNav.update((v) => !v);
  }

  closeMobileNav(): void {
    this.showMobileNav.set(false);
  }

  toggleNotifications(): void {
    this.notificationsOpen.update((v) => !v);
    if (this.notificationsOpen()) this.profileMenuOpen.set(false);
  }

  closeNotifications(): void {
    this.notificationsOpen.set(false);
  }

  toggleProfileMenu(): void {
    this.profileMenuOpen.update((v) => !v);
    if (this.profileMenuOpen()) this.notificationsOpen.set(false);
  }

  closeProfileMenu(): void {
    this.profileMenuOpen.set(false);
  }

  onLogout(): void {
    // The notification socket is torn down automatically when auth clears (the service
    // watches currentUser), so no explicit stop() is needed here.
    this.authService.logout().subscribe({
      next: () => {
        // console.log('Logout API success.');
        this.profileMenuOpen.set(false);
        this.router.navigate(['/auth/login']);
      },
      error: (err) => {
        console.error('Logout failed with error:', err);
        // Ensure menu is closed and redirected even if server call fails
        this.profileMenuOpen.set(false);
        this.router.navigate(['/auth/login']);
      }
    });
  }

  onIntegrations(): void {
    this.profileMenuOpen.set(false);
    this.router.navigate(['/app/integrations']);
  }

  onSettings(): void {
    this.profileMenuOpen.set(false);
    this.router.navigate(['/app/settings']);
  }

  onMarkAllRead(): void {
    // Marking read is a quiet, expected action — no success toast. Errors still surface.
    this.notificationsSvc.markAllRead().subscribe({
      error: () => this.toast.error('Could not mark all as read. Please try again.', 'Error'),
    });
  }

  /** Clicking an unread notification marks just that one as read (silent revert on failure). */
  onNotificationClick(n: { id: string; read: boolean; kind?: string }): void {
    if (!n.read) this.notificationsSvc.markRead(n.id).subscribe({ error: () => {} });
    // The one notification with a place to act on it.
    if (n.kind === 'spam_shield') {
      this.notificationsOpen.set(false);
      this.router.navigate(['/app/transactions/spam']);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.notificationsOpen.set(false);
    this.profileMenuOpen.set(false);
    this.closeFab();
  }
}
